"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Card,
  Typography,
  Select,
  MenuItem,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Divider,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Switch, // Import Switch for the toggle
  FormControlLabel, // Import FormControlLabel for the switch
  TableContainer, // Import TableContainer for the table container component
} from "@mui/material"
import {
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  CalendarToday as CalendarTodayIcon,
  Home as HomeIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import axios from "axios"

const baseUrl = process.env.REACT_APP_APP_URL

const AdminCompetitionInvoice = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [horses, setHorses] = useState([])
  const [selectedUser, setSelectedUser] = useState("")
  const [selectedHorseId, setSelectedHorseId] = useState("")
  const [selectedTests, setSelectedTests] = useState([])
  const [selectedFixedCosts, setSelectedFixedCosts] = useState([])
  const [total, setTotal] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [quantities, setQuantities] = useState({})
  const [customPrices, setCustomPrices] = useState({})
  const [savedInvoiceItems, setSavedInvoiceItems] = useState([])
  const [allUserInvoices, setAllUserInvoices] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingItems, setLoadingItems] = useState(false)
  const [loadingAllInvoices, setLoadingAllInvoices] = useState(false)
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState(null)
  const [invoiceUser, setInvoiceUser] = useState(null) // For registered user
  const [invoiceClientName, setInvoiceClientName] = useState(null) // For guest client name
  const [invoiceClientEmail, setInvoiceClientEmail] = useState(null) // For guest client email
  const [currentInvoiceHorseName, setCurrentInvoiceHorseName] = useState("")
  const [paymentStatus, setPaymentStatus] = useState("non payé")
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState({ invoiceNumber: "", currentStatus: "" })
  const token = localStorage.getItem("CC_Token")
  const [updatingStatus, setUpdatingStatus] = useState(false)

  // New states for guest client input
  const [isGuestClient, setIsGuestClient] = useState(false)
  const [guestClientName, setGuestClientName] = useState("")
  const [guestClientEmail, setGuestClientEmail] = useState("")

  const testPrices = {
    Initiation: 50,
    Qualification: 60,
    "CSO*": 70,
    "CSO**": 80,
  }

  const fixedCosts = {
    Transport: 300,
    Groom: 30,
    "Logement cheval": 50,
    "Passage de grade": 60,
  }

  useEffect(() => {
    fetchUsers()
    fetchHorses()
    fetchAllInvoices()
  }, [])

  useEffect(() => {
    calculateTotal()
  }, [selectedTests, selectedFixedCosts, quantities, customPrices])

  useEffect(() => {
    if (currentInvoiceNumber) {
      fetchInvoiceItems(currentInvoiceNumber)
    }
  }, [currentInvoiceNumber, horses])

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/users/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (Array.isArray(response.data.members)) {
        setUsers(response.data.members)
      } else {
        console.error("Invalid response format: users data is not an array")
        setUsers([])
      }
    } catch (error) {
      console.error("Error fetching users:", error)
      toast.error("Erreur lors de la récupération des utilisateurs")
    }
  }

  const fetchHorses = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/horses`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.data.status === "success" && Array.isArray(response.data.data)) {
        setHorses(response.data.data)
      } else {
        console.error("Invalid response format: horses data is not an array", response.data)
        setHorses([])
      }
    } catch (error) {
      console.error("Error fetching horses:", error)
      toast.error("Erreur lors de la récupération des chevaux")
    }
  }

  const fetchAllInvoices = async () => {
    setLoadingAllInvoices(true)
    try {
      const response = await axios.get(`${baseUrl}/api/invoice-items/all`, {
        // Changed endpoint to fetch all invoice items
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.success && Array.isArray(response.data.invoices)) {
        setAllUserInvoices(response.data.invoices)
      } else {
        console.error("Invalid response format:", response.data)
        setAllUserInvoices([])
      }
    } catch (error) {
      console.error("Error fetching all invoices:", error)
      toast.error("Erreur lors de la récupération des factures")
    } finally {
      setLoadingAllInvoices(false)
    }
  }

  const fetchInvoiceItems = async (invoiceNumber) => {
    if (!invoiceNumber) return

    setLoadingItems(true)
    try {
      const response = await axios.get(`${baseUrl}/api/invoice-items/${invoiceNumber}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.success) {
        setSavedInvoiceItems(response.data.items)
        setInvoiceUser(response.data.user) // Will be null if guest client
        setInvoiceClientName(response.data.client_name) // Set guest client name
        setInvoiceClientEmail(response.data.client_email) // Set guest client email
        setPaymentStatus(response.data.payment_status)

        const invoiceHorseId = response.data.horse_id // Assuming horse_id might still be returned or derived
        if (invoiceHorseId && horses.length > 0) {
          const horse = horses.find((h) => h.id === invoiceHorseId)
          setCurrentInvoiceHorseName(horse ? horse.name : "N/A")
        } else if (response.data.horse_name) {
          // If horse_name is directly available
          setCurrentInvoiceHorseName(response.data.horse_name)
        } else {
          setCurrentInvoiceHorseName("")
        }
      }
    } catch (error) {
      console.error("Error fetching invoice items:", error)
      toast.error("Erreur lors de la récupération des éléments de la facture")
    } finally {
      setLoadingItems(false)
    }
  }

  const calculateTotal = () => {
    const testsTotal = selectedTests.reduce((sum, test) => {
      const quantity = quantities[`test_${test}`] || 1
      const price = customPrices[`test_${test}`] !== undefined ? customPrices[`test_${test}`] : testPrices[test]
      return sum + price * quantity
    }, 0)

    const fixedTotal = selectedFixedCosts.reduce((sum, costName) => {
      const quantity = quantities[costName] || 1
      const price = customPrices[costName] !== undefined ? customPrices[costName] : fixedCosts[costName]
      return sum + price * quantity
    }, 0)

    setTotal(testsTotal + fixedTotal)
  }

  const handleTestChange = (event) => {
    const newSelectedTests = event.target.value
    setSelectedTests(newSelectedTests)

    const newQuantities = { ...quantities }
    newSelectedTests.forEach((test) => {
      if (!newQuantities[`test_${test}`]) {
        newQuantities[`test_${test}`] = 1
      }
    })

    Object.keys(newQuantities).forEach((key) => {
      if (key.startsWith("test_") && !newSelectedTests.includes(key.replace("test_", ""))) {
        delete newQuantities[key]
      }
    })

    setQuantities(newQuantities)
  }

  const handleFixedCostChange = (event) => {
    const selectedCosts = event.target.value
    setSelectedFixedCosts(selectedCosts)

    const newQuantities = { ...quantities }
    selectedCosts.forEach((cost) => {
      if (!newQuantities[cost]) {
        newQuantities[cost] = 1
      }
    })

    Object.keys(newQuantities).forEach((cost) => {
      if (!cost.startsWith("test_") && !selectedCosts.includes(cost)) {
        delete newQuantities[cost]
      }
    })

    setQuantities(newQuantities)
  }

  const handleQuantityChange = (itemKey, value) => {
    const quantity = Number.parseInt(value) || 1
    setQuantities({
      ...quantities,
      [itemKey]: quantity > 0 ? quantity : 1,
    })
  }

  const handlePriceChange = (itemKey, value) => {
    const price = Number.parseFloat(value) || 0
    setCustomPrices({
      ...customPrices,
      [itemKey]: price >= 0 ? price : 0,
    })
  }

  const getItemPrice = (itemKey, defaultPrice) => {
    return customPrices[itemKey] !== undefined ? customPrices[itemKey] : defaultPrice
  }

  const handleSubmit = async () => {
    // Validation for client selection
    if (!isGuestClient && !selectedUser) {
      toast.error("Veuillez sélectionner un utilisateur ou activer le mode client non enregistré.", {
        position: "top-right",
        autoClose: 3000,
      })
      return
    }
    if (isGuestClient && !guestClientName.trim()) {
      toast.error("Veuillez entrer le nom du client non enregistré.", {
        position: "top-right",
        autoClose: 3000,
      })
      return
    }

    if (!selectedHorseId) {
      toast.error("Veuillez sélectionner un cheval", {
        position: "top-right",
        autoClose: 3000,
      })
      return
    }

    if (selectedTests.length === 0 && selectedFixedCosts.length === 0) {
      toast.error("Veuillez sélectionner au moins un élément pour la facture", {
        position: "top-right",
        autoClose: 3000,
      })
      return
    }

    setLoading(true)
    try {
      const items = []
      const selectedHorseName = horses.find((h) => h.id === selectedHorseId)?.name || "N/A"

      selectedTests.forEach((test) => {
        const price = getItemPrice(`test_${test}`, testPrices[test])
        items.push({
          item_type: "test",
          item_name: `Épreuve ${test}`,
          price: price,
          quantity: quantities[`test_${test}`] || 1,
        })
      })

      selectedFixedCosts.forEach((cost) => {
        const price = getItemPrice(cost, fixedCosts[cost])
        items.push({
          item_type: "fixed_cost",
          item_name: cost,
          price: price,
          quantity: quantities[cost] || 1,
        })
      })

      const payload = {
        horse_name: selectedHorseName,
        items: items,
      }

      if (isGuestClient) {
        payload.client_name = guestClientName.trim()
        payload.client_email = guestClientEmail.trim() || null // Allow email to be optional
        payload.user_id = null // Explicitly set user_id to null for guest clients
      } else {
        payload.user_id = selectedUser
        payload.client_name = null // Explicitly set client_name to null for registered users
        payload.client_email = null // Explicitly set client_email to null for registered users
      }

      const response = await axios.post(`${baseUrl}/api/invoice-items`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.success) {
        toast.success("Facture créée avec succès", {
          position: "top-right",
          autoClose: 3000,
        })

        setCurrentInvoiceNumber(response.data.invoice_number)
        fetchAllInvoices()
        handleReset()
      }
    } catch (error) {
      console.error("Error creating invoice:", error.response?.data || error.message)
      toast.error(`Erreur lors de la création de la facture: ${error.response?.data?.message || error.message}`, {
        position: "top-right",
        autoClose: 5000,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    fetchAllInvoices()
    if (currentInvoiceNumber) {
      fetchInvoiceItems(currentInvoiceNumber)
    }
  }

  const handleReset = () => {
    setSelectedUser("")
    setSelectedHorseId("")
    setSelectedTests([])
    setSelectedFixedCosts([])
    setQuantities({})
    setCustomPrices({})
    setTotal(0)
    setIsGuestClient(false) // Reset guest client toggle
    setGuestClientName("") // Reset guest client name
    setGuestClientEmail("") // Reset guest client email
  }

  const handlePaymentStatusChange = (invoiceNumber, currentStatus) => {
    setConfirmDialogData({
      invoiceNumber,
      currentStatus,
    })
    setConfirmDialogOpen(true)
  }

  const handleConfirmStatusChange = async () => {
    const { invoiceNumber, currentStatus } = confirmDialogData
    if (!invoiceNumber) return

    setUpdatingStatus(true)
    try {
      const newStatus = currentStatus === "payé" ? "non payé" : "payé"

      const response = await axios.put(
        `${baseUrl}/api/invoice-items/payment-status`,
        {
          invoice_number: invoiceNumber,
          payment_status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        if (invoiceNumber === currentInvoiceNumber) {
          setPaymentStatus(newStatus)
        }

        setAllUserInvoices((prevInvoices) =>
          prevInvoices.map((invoice) =>
            invoice.invoice_number === invoiceNumber ? { ...invoice, payment_status: newStatus } : invoice,
          ),
        )

        toast.success(`Statut de paiement mis à jour: ${newStatus}`, {
          position: "top-right",
          autoClose: 3000,
        })
      }
    } catch (error) {
      console.error("Error updating payment status:", error)
      toast.error("Erreur lors de la mise à jour du statut de paiement", {
        position: "top-right",
        autoClose: 3000,
      })
    } finally {
      setUpdatingStatus(false)
      setConfirmDialogOpen(false)
    }
  }

  const handleViewInvoice = (invoiceNumber) => {
    setCurrentInvoiceNumber(invoiceNumber)
  }

  const filteredUsers = users.filter((user) =>
    `${user.name} (${user.email})`.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleNavigateToNotifications = () => {
    navigate("/AdminGetNotifs")
  }

  const handleLogout = () => {
    localStorage.removeItem("CC_Token")
    localStorage.removeItem("user")
    toast.success("Vous avez été déconnecté avec succès.", {
      position: "top-right",
      autoClose: 3000,
    })
    setTimeout(() => navigate("/login"), 4000)
  }

  const handleProfile = () => {
    navigate("/ProfileAdmin")
  }

  const calculateInvoiceTotal = () => {
    return savedInvoiceItems.reduce((sum, item) => sum + Number.parseFloat(item.total_price), 0).toFixed(2)
  }

  return (
    <div className="app">
      <ToastContainer />

      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar
            alt="Logo"
            src={"/placeholder.svg?height=40&width=40&query=club equestre jafoura logo"}
            sx={{ width: 40, height: 40, marginRight: "0.5rem" }}
          />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          p: 3,
          mt: 8,
          mb: 8,
          backgroundColor: "#f5f5f5",
          minHeight: "calc(100vh - 128px)",
          overflowX: "auto",
        }}
      >
        <Card
          sx={{
            p: 4,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: "#003366",
              fontWeight: "500",
              mb: 2,
              textAlign: "center",
            }}
          >
            Facture de Concours
          </Typography>

          {/* Create Invoice Form */}
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isGuestClient}
                    onChange={(e) => {
                      setIsGuestClient(e.target.checked)
                      // Clear user selection if switching to guest mode
                      if (e.target.checked) {
                        setSelectedUser("")
                        setSearchTerm("")
                      } else {
                        // Clear guest client details if switching to registered user mode
                        setGuestClientName("")
                        setGuestClientEmail("")
                      }
                    }}
                    color="primary"
                  />
                }
                label="Client non enregistré"
                sx={{ mb: 2 }}
              />
            </Grid>

            {isGuestClient ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nom du client *"
                    variant="outlined"
                    fullWidth
                    value={guestClientName}
                    onChange={(e) => setGuestClientName(e.target.value)}
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#f8f9fa",
                        "&:hover": {
                          backgroundColor: "#ffffff",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email du client (optionnel)"
                    variant="outlined"
                    fullWidth
                    type="email"
                    value={guestClientEmail}
                    onChange={(e) => setGuestClientEmail(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#f8f9fa",
                        "&:hover": {
                          backgroundColor: "#ffffff",
                        },
                      },
                    }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Rechercher un utilisateur"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#f8f9fa",
                        "&:hover": {
                          backgroundColor: "#ffffff",
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Utilisateur *</InputLabel>
                    <Select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      label="Utilisateur *"
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#f8f9fa",
                        "&:hover": {
                          backgroundColor: "#ffffff",
                        },
                      }}
                    >
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {`${user.name} (${user.email})`}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>Aucun utilisateur trouvé</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* New Horse Select */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Cheval *</InputLabel>
                <Select
                  value={selectedHorseId}
                  onChange={(e) => setSelectedHorseId(e.target.value)}
                  label="Cheval *"
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#f8f9fa",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                    },
                  }}
                >
                  {horses.length > 0 ? (
                    horses.map((horse) => (
                      <MenuItem key={horse.id} value={horse.id}>
                        {horse.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Aucun cheval trouvé</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Épreuves</InputLabel>
                <Select
                  multiple
                  value={selectedTests}
                  onChange={handleTestChange}
                  label="Épreuves"
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#f8f9fa",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                    },
                  }}
                >
                  {Object.keys(testPrices).map((test) => (
                    <MenuItem key={test} value={test}>
                      {test}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Services additionnels</InputLabel>
                <Select
                  multiple
                  value={selectedFixedCosts}
                  onChange={handleFixedCostChange}
                  label="Services additionnels"
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#f8f9fa",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                    },
                  }}
                >
                  {Object.keys(fixedCosts).map((cost) => (
                    <MenuItem key={cost} value={cost}>
                      {cost}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  overflow: "auto",
                  maxWidth: "100%",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f0f5fa" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Élément</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Quantité</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Prix unitaire (TND)</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        Total (TND)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedFixedCosts.map((costName) => (
                      <TableRow
                        key={costName}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:hover": { backgroundColor: "#f8f9fa" },
                        }}
                      >
                        <TableCell sx={{ fontSize: "1rem" }}>{costName}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={quantities[costName] || 1}
                            onChange={(e) => handleQuantityChange(costName, e.target.value)}
                            InputProps={{
                              inputProps: { min: 1 },
                            }}
                            size="small"
                            sx={{ width: "80px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={getItemPrice(costName, fixedCosts[costName])}
                            onChange={(e) => handlePriceChange(costName, e.target.value)}
                            InputProps={{
                              inputProps: { min: 0, step: "0.1" },
                            }}
                            size="small"
                            sx={{ width: "100px" }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "1rem", color: "#003366" }}>
                          {(getItemPrice(costName, fixedCosts[costName]) * (quantities[costName] || 1)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {selectedTests.map((test) => {
                      const itemKey = `test_${test}`
                      return (
                        <TableRow
                          key={test}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": { backgroundColor: "#f8f9fa" },
                          }}
                        >
                          <TableCell sx={{ fontSize: "1rem" }}>Épreuve {test}</TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={quantities[itemKey] || 1}
                              onChange={(e) => handleQuantityChange(itemKey, e.target.value)}
                              InputProps={{
                                inputProps: { min: 1 },
                              }}
                              size="small"
                              sx={{ width: "80px" }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={getItemPrice(itemKey, testPrices[test])}
                              onChange={(e) => handlePriceChange(itemKey, e.target.value)}
                              InputProps={{
                                inputProps: { min: 0, step: "0.1" },
                              }}
                              size="small"
                              sx={{ width: "100px" }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "1rem", color: "#003366" }}>
                            {(getItemPrice(itemKey, testPrices[test]) * (quantities[itemKey] || 1)).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    <TableRow
                      sx={{
                        backgroundColor: "#f8f9fa",
                        "&:hover": { backgroundColor: "#f8f9fa" },
                      }}
                    >
                      <TableCell
                        colSpan={3}
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: "500",
                          color: "#003366",
                          textAlign: "right",
                        }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: "500",
                          color: "#003366",
                        }}
                      >
                        {total.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  fullWidth
                  disabled={loading}
                  sx={{
                    height: "48px",
                    borderRadius: "8px",
                    backgroundColor: "#003366",
                    fontSize: "1rem",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#002244",
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Enregistrer la facture"}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleReset}
                  fullWidth
                  sx={{
                    height: "48px",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    textTransform: "none",
                    borderColor: "#003366",
                    color: "#003366",
                    "&:hover": {
                      borderColor: "#002244",
                      backgroundColor: "#f0f5fa",
                    },
                  }}
                >
                  Réinitialiser
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {/* Saved Invoice Items Table - Itemized Format */}
          {currentInvoiceNumber && (
            <Grid item xs={12} sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: "#003366",
                    fontWeight: "500",
                  }}
                >
                  Facture sélectionnée
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: "#666" }}>
                    N°: {currentInvoiceNumber}
                  </Typography>
                  <Chip
                    label={paymentStatus === "payé" ? "Payé" : "Non payé"}
                    color={paymentStatus === "payé" ? "success" : "error"}
                    onClick={() => handlePaymentStatusChange(currentInvoiceNumber, paymentStatus)}
                    sx={{ cursor: "pointer" }}
                  />
                  <IconButton onClick={handleRefresh} color="primary">
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Box>

              {loadingItems ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: "12px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    overflow: "auto",
                    margin: "0 auto",
                  }}
                >
                  <Table sx={{ borderCollapse: "collapse", minWidth: 650 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f0f5fa" }}>
                        <TableCell sx={{ fontWeight: "bold", border: "1px solid #e0e0e0" }}>Élément</TableCell>
                        <TableCell sx={{ fontWeight: "bold", textAlign: "center", border: "1px solid #e0e0e0" }}>
                          Quantité
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", textAlign: "right", border: "1px solid #e0e0e0" }}>
                          Prix unitaire (TND)
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", textAlign: "right", border: "1px solid #e0e0e0" }}>
                          Total (TND)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Customer Name Row - First line in the table */}
                      {(invoiceUser || invoiceClientName) && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              backgroundColor: "#e6f0ff",
                              fontWeight: "bold",
                              color: "#003366",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            Client:{" "}
                            {invoiceUser
                              ? `${invoiceUser.name} (${invoiceUser.email})`
                              : `${invoiceClientName} ${invoiceClientEmail ? `(${invoiceClientEmail})` : ""}`}
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Horse Name Row */}
                      {currentInvoiceHorseName && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              backgroundColor: "#f0f5fa",
                              fontWeight: "bold",
                              color: "#003366",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            Cheval: {currentInvoiceHorseName}
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Individual Invoice Items */}
                      {savedInvoiceItems.map((item) => (
                        <TableRow
                          key={item.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": { backgroundColor: "#f8f9fa" },
                          }}
                        >
                          <TableCell sx={{ fontSize: "1rem", border: "1px solid #e0e0e0" }}>{item.item_name}</TableCell>
                          <TableCell align="center" sx={{ border: "1px solid #e0e0e0" }}>
                            {item.quantity}
                          </TableCell>
                          <TableCell align="right" sx={{ border: "1px solid #e0e0e0" }}>
                            {Number.parseFloat(item.price).toFixed(2)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "500", color: "#003366", border: "1px solid #e0e0e0" }}
                          >
                            {Number.parseFloat(item.total_price).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow
                        sx={{
                          backgroundColor: "#f8f9fa",
                          "&:hover": { backgroundColor: "#f8f9fa" },
                        }}
                      >
                        <TableCell
                          colSpan={3}
                          sx={{
                            fontSize: "1.1rem",
                            fontWeight: "500",
                            color: "#003366",
                            textAlign: "right",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          Total de la facture
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: "1.1rem",
                            fontWeight: "500",
                            color: "#003366",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          {calculateInvoiceTotal()} TND
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          )}

          {/* All Invoices Section - Consolidated Table */}
          <Grid container spacing={4}>
            <Grid item xs={12} sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: "#003366",
                    fontWeight: "500",
                  }}
                >
                  Toutes les factures
                </Typography>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                  Rafraîchir
                </Button>
              </Box>

              {loadingAllInvoices ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: "12px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    overflow: "auto",
                    margin: "0 auto",
                  }}
                >
                  <Table sx={{ borderCollapse: "collapse", minWidth: 750 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f0f5fa" }}>
                        <TableCell sx={{ fontWeight: "bold", border: "1px solid #e0e0e0" }}>Client</TableCell>
                        <TableCell sx={{ fontWeight: "bold", border: "1px solid #e0e0e0" }}>Cheval</TableCell>
                        <TableCell sx={{ fontWeight: "bold", border: "1px solid #e0e0e0" }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: "bold", textAlign: "right", border: "1px solid #e0e0e0" }}>
                          Montant
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", textAlign: "center", border: "1px solid #e0e0e0" }}>
                          Statut
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", textAlign: "center", border: "1px solid #e0e0e0" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allUserInvoices.length > 0 ? (
                        allUserInvoices.map((invoice, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                              "&:hover": { backgroundColor: "#f8f9fa" },
                            }}
                          >
                            <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                              {invoice.user
                                ? `${invoice.user.name} (${invoice.user.email})`
                                : `${invoice.client_name} ${invoice.client_email ? `(${invoice.client_email})` : ""}`}
                            </TableCell>
                            <TableCell sx={{ border: "1px solid #e0e0e0" }}>{invoice.horse_name || "N/A"}</TableCell>
                            <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                              {new Date(invoice.created_at).toLocaleDateString("fr-FR")}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: "500", border: "1px solid #e0e0e0" }}>
                              {invoice.total_amount ? invoice.total_amount.toFixed(2) : "0.00"} TND
                            </TableCell>
                            <TableCell align="center" sx={{ border: "1px solid #e0e0e0" }}>
                              <Chip
                                label={invoice.payment_status === "payé" ? "Payé" : "Non payé"}
                                color={invoice.payment_status === "payé" ? "success" : "error"}
                                onClick={() =>
                                  handlePaymentStatusChange(invoice.invoice_number, invoice.payment_status)
                                }
                                sx={{ cursor: "pointer" }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ border: "1px solid #e0e0e0" }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleViewInvoice(invoice.invoice_number)}
                              >
                                Voir
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: "center", border: "1px solid #e0e0e0" }}>
                            <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
                              Aucune facture trouvée.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Confirmation Dialog for Payment Status Change */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmation de changement de statut"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir marquer cette facture comme{" "}
            {confirmDialogData.currentStatus === "payé" ? "non payée" : "payée"} ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
            Annuler
          </Button>
          <Button
            onClick={handleConfirmStatusChange}
            color="primary"
            variant="contained"
            autoFocus
            disabled={updatingStatus}
          >
            {updatingStatus ? <CircularProgress size={24} /> : "Confirmer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
        <BottomNavigation
          sx={{
            backgroundColor: "#003366",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <BottomNavigationAction
            label="Calendrier"
            icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminChrono")}
          />
          <BottomNavigationAction
            label="Home"
            icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/Admin")}
          />
          <BottomNavigationAction
            label="Paiement"
            icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminPaiment")}
          />
        </BottomNavigation>
      </Box>
    </div>
  )
}

export default AdminCompetitionInvoice
